<i18n>
{
  "en": {
    "availableTitle": "{title} is available",
    "pageCreated": "Your page has been created!",
    "takenTitle": "{title} is already taken"
  },
  "fr": {
    "availableTitle": "{title} est disponible",
    "pageCreated": "Votre page a été créée",
    "takenTitle": "{title} est déjà pris"
  }
}
</i18n>
<template>
  <v-dialog v-model="dialog" width="700" persistent>
    <template v-slot:activator="{ on }">
      <v-btn
        color="primary"
        class="mr-5"
        v-on="on"
      >
        <v-icon left>mdi-plus</v-icon>
        Create a Page
      </v-btn>
    </template>
    <v-card >
      <v-form @submit.prevent="createPage" ref="addpageDialog">
        <v-card-title
          class="headline"
          primary-title
        >
          Create a Page
        </v-card-title>
        <v-card-text>
           <v-select
            :items="connections"
            label="Choose a connection"
            persistent-hint
            hint="In what drive do you want to receive files?"
            item-text="decryptedGoogleName"
            item-value="id"
            v-model="page.connectionId"
          ></v-select>
          <v-text-field
            name="title"
            label="Folder Name"
            v-model="page.title"
            @input="onTitleInput"
            :rules="titleRules"
            :loading="checkTitleIsUniqueLoading"
            :error-messages="isUniqueErrorMessage"
            :success-messages="validTitleMessage"
          >
          </v-text-field>

          <v-text-field
            filled
            persistent-hint
            hint="Share this URL to receive files"
            placeholder="URL to your page"
            readonly
            :value="slugifiedTitle">
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            color="primary"
            text
            @click="close"
            :disabled="loading"
          >{{ $t('common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            type="submit"
            text
            :disabled="isSubmitDisabled"
            :loading="loading"
          >
            {{ $t('common.submit') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { maxLength, required } from 'vuelidate/lib/validators'
import api from '@/services/api/pages.api'
import slugify from 'slugify'
import _ from 'lodash'

export default {
  name: 'CreatePageDialog',
  data: () => ({
    appUrl: process.env.VUE_APP_URL,
    checkTitleIsUniqueLoading: false,
    dialog: false,
    isUnique: true,
    isUniqueErrorMessage: '',
    loading: false,
    page: {
      title: '',
      connectionId: null
    },
    validTitleMessage: ''
  }),
  watch: {
    page: {
      handler: function (v) {
        if (v) this.debouncedCheck(v.title)
      },
      deep: true
    }
  },
  async created () {
    await this.$store.dispatch('getOrganizationConnections')
    if (this.connections && this.connections.length > 0) {
      this.page.connectionId = this.connections[0].id
    }

    this.debouncedCheck = _.debounce(async (value) => {
      this.validTitleMessage = ''
      this.isUniqueErrorMessage = ''
      if (value === '') {
        this.isUnique = true
      } else {
        const slug = slugify(value, {
          lower: true,
          remove: /[*+~%<>/;.(){}?,'"!:@#^|]/g
        }).substr(0, 255)
        this.checkTitleIsUniqueLoading = true
        const { data } = await api.getPageBySlug(slug)
        this.checkTitleIsUniqueLoading = false
        this.isUnique = !data.page
        if (data.page) {
          this.isUniqueErrorMessage = this.$t('takenTitle', { title: `${this.appUrl}page/${slug}` })
        } else {
          this.validTitleMessage = this.$t('availableTitle', { title: `${this.appUrl}page/${slug}` })
        }
      }
    }, 1000)
  },
  computed: {
    connections () {
      return this.$store.getters.getConnections
    },
    isSubmitDisabled () {
      return this.$v.$invalid || this.checkTitleIsUniqueLoading
    },
    slugifiedTitle () {
      const slugifiedTitle = slugify(this.page.title, {
        lower: true,
        remove: /[*+~%<>/;.(){}?,'"!:@#^|]/g
      })
      return `${this.appUrl}page/${slugifiedTitle.substr(0, 255)}`
    },
    titleRules () {
      return [
        () => this.$v.page.title.required || this.$t('validation.required'),
        () => this.$v.page.title.maxLength || this.$t('validation.maxLength', { max: 255 })
      ]
    }
  },
  methods: {
    close () {
      this.dialog = false
    },
    async createPage () {
      this.loading = true
      await this.$store.dispatch('createOrganizationPage', {
        title: this.page.title,
        connectionId: this.page.connectionId
      })
      this.loading = false
      this.$root.$snackbar.show(this.$t('pageCreated'))
      this.dialog = false
    },
    onTitleInput () {
      this.$v.page.title.$touch()
      this.checkTitleIsUniqueLoading = true
      this.isUniqueErrorMessage = ''
      this.validTitleMessage = ''
    }
  },
  validations: {
    page: {
      title: {
        isUnique () {
          return this.isUnique
        },
        maxLength: maxLength(255),
        required
      }
    }
  }
}
</script>
